import { useQueries, UseQueryResult } from '@tanstack/react-query'
import { WebAppVersionDetails, WebAppVersions, webAppVersionsSchema } from '@mobi/api-types'

/**
 * Retrieve available web app versions matching the specified major versions
 */
export const useVersions = (majorVersions: number[]) => {
  const results = useQueries({
    queries: majorVersions.map(version => ({
      queryKey: [`webapp/versions/${version}`],
      queryFn: () => queryFn(version),
      staleTime: 1000 * 60 /* 1 minute */,
      retry: 0,
    })),
  })

  if (results.some(result => result.isLoading)) {
    return {
      isLoading: true,
      isError: false,
      data: undefined,
    }
  }

  if (results.every(result => result.isError)) {
    return {
      isLoading: false,
      isError: true,
      data: undefined,
    }
  }

  const successfulResults = results.filter(r => !r.isError)

  return {
    isLoading: false,
    isError: false,
    data: merge(successfulResults),
  }
}

const queryFn = async (majorVersion: number) => {
  const response = await fetch(`api/support/webapp/versions?majorVersion=${majorVersion}`)
  const json = await response.json()
  return webAppVersionsSchema.parse(json)
}

/**
 * Merge versions from multiple queries into a single dictionary
 */
const merge = (
  responses: UseQueryResult<WebAppVersions>[]
): Record<string, WebAppVersionDetails> => {
  return responses
    .flatMap(response => response.data || [])
    .reduce((accumulator, data) => {
      return Object.assign(accumulator, data.versions)
    }, {})
}
